import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Input,
  PageShell,
  Pagination,
  RadixSelect,
  SearchGrid,
  SearchGridItem,
  Skeleton,
  type DataTableColumn,
  type DataTableSort,
} from '@/components/ui';
import type { AdminRole, Money } from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import { ArtistSelector } from '@/features/catalog/components/ArtistSelector';
import { canPerformFundingWrite } from '../lib/fundingActionRbac';
import { FundingStatusPill } from '../components/FundingStatusPill';
import { PAGE_SIZE, useFundingRows } from '../hooks/useFundingRows';
import {
  EMPTY_FUNDING_FILTER,
  FUNDING_CATEGORY_VALUES,
  FUNDING_STATUS_VALUES,
  isFundingPublic,
  kstDate,
  type FundingCategory,
  type FundingListFilter,
  type FundingListSort,
  type FundingRow,
  type FundingSortKey,
  type FundingVisibility,
} from '../lib/fundingListView';
import { GOAL_UNSET_LABEL, isGoalUnset } from '../lib/fundingGoal';

const SORTABLE: ReadonlySet<string> = new Set<FundingSortKey>(['achievementRate', 'launchedAt', 'closedAt']);

const goalText = (m: Money | null | undefined): string => (isGoalUnset(m) ? GOAL_UNSET_LABEL : money(m));
const money = (m: Money | null | undefined): string =>
  m ? `${new Intl.NumberFormat('ko-KR').format(m.amount)} ${m.currencyCode}` : '—';
const count = (n: number | null | undefined): string => (n == null ? '—' : n.toLocaleString('ko-KR'));
const day = (iso: string | null | undefined): string => kstDate(iso) || '—';

/** 체크박스·라디오 묶음(검색 조건) — 고도몰 검색 영역처럼. */
const Choice = ({ children, testId }: { children: React.ReactNode; testId: string }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]" data-testid={testId}>
    {children}
  </div>
);

/*
 * 펀딩 캠페인 목록. QA-398:
 * - 검색 조건: 상품 관리와 같은 헤더|입력 2열 표. 검색어(제목) · 상태(다중) / 아티스트 · 구분 / 펀딩 기간 · 공개 여부 /
 *   런칭일 · 마감일. 검색·정렬·페이지는 서버(useFundingRows, settlement #9).
 * - 컬럼: No · 제목 · 구분 · 아티스트 · 상태 · 공개 여부 · 달성 금액 / 목표 금액 · 달성률↕ · 참여 수 · 리워드 · 기간 ·
 *   런칭일↕ · 마감일↕. 종전 맨 앞 [-]는 목록 응답에 없는 대표 이미지 자리였다.
 */
export const FundingCampaignsPage = () => {
  const { t } = useTranslation('funding');
  const { currentAdmin } = useAuthContext();
  const canWrite = canPerformFundingWrite((currentAdmin?.roles ?? []) as ReadonlyArray<AdminRole>);
  const [draft, setDraft] = useState<FundingListFilter>(EMPTY_FUNDING_FILTER);
  const [filter, setFilter] = useState<FundingListFilter>(EMPTY_FUNDING_FILTER);
  const [sort, setSort] = useState<FundingListSort | null>(null);
  const [page, setPage] = useState(1);
  const { rows: pageRows, total, isPending, isError, refetch } = useFundingRows(filter, sort, page);
  const patch = (p: Partial<FundingListFilter>) => setDraft((prev) => ({ ...prev, ...p }));
  const apply = (next: FundingListFilter) => {
    setFilter(next);
    setPage(1);
  };


  const statusLabel = (s: string) => t(`funding.status.${s}`, s);
  const categoryLabel = (c: string | null | undefined) => (c ? t(`funding.category.${c}`, c) : '—');

  const columns: DataTableColumn<FundingRow>[] = [
    {
      key: 'no',
      header: t('funding.columns.no', 'No'),
      align: 'center',
      width: 56,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {(page - 1) * PAGE_SIZE + pageRows.indexOf(row) + 1}
        </span>
      ),
    },
    {
      key: 'title',
      header: t('funding.columns.title', '제목'),
      cell: (row) => (
        <Link
          to={`/funding-campaigns/${row.campaignId}`}
          className="block min-w-[200px] text-sm font-medium hover:underline"
          style={{ color: 'var(--content-strong)' }}
          data-testid="funding-title-link"
        >
          {row.title}
        </Link>
      ),
    },
    { key: 'category', header: t('funding.columns.category', '구분'), width: 84, cell: (row) => <span className="whitespace-nowrap text-[12px]">{categoryLabel(row.category)}</span> },
    { key: 'artist', header: t('funding.columns.artist', '아티스트'), width: 120, cell: (row) => <span className="text-[12px]">{row.artistName || '—'}</span> },
    {
      key: 'status',
      header: t('funding.columns.status', '상태'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <span className="inline-block whitespace-nowrap">
          <FundingStatusPill status={row.status} campaignId={row.campaignId} />
        </span>
      ),
    },
    {
      key: 'visibility',
      header: t('funding.columns.visibility', '공개 여부'),
      align: 'center',
      width: 84,
      cell: (row) => (
        <span className="whitespace-nowrap text-[12px]" data-testid="funding-visibility">
          {isFundingPublic(row.status) ? t('funding.visibility.public', '공개') : t('funding.visibility.private', '비공개')}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('funding.columns.goal', '달성 금액 / 목표 금액'),
      align: 'right',
      width: 170,
      cell: (row) => (
        <div className="whitespace-nowrap text-right tabular-nums">
          <div className="text-sm" style={{ color: 'var(--content-strong)' }}>{money(row.currentAmount)}</div>
          <div className="text-[11px]" style={{ color: 'var(--content-muted)' }}>/ {goalText(row.goalAmount)}</div>
        </div>
      ),
    },
    {
      key: 'achievementRate',
      header: t('funding.columns.achievement', '달성률'),
      align: 'right',
      width: 84,
      sortable: true,
      cell: (row) => (
        <span className="whitespace-nowrap text-[12px] tabular-nums">
          {isGoalUnset(row.goalAmount) ? GOAL_UNSET_LABEL : row.achievementRate == null ? '—' : `${row.achievementRate.toFixed(1)}%`}
        </span>
      ),
    },
    { key: 'pledgeCount', header: t('funding.columns.pledgeCount', '참여 수'), align: 'right', width: 72, cell: (row) => <span className="text-[12px] tabular-nums" data-testid="funding-pledge-count">{count(row.pledgeCount)}</span> },
    { key: 'rewardCount', header: t('funding.columns.rewardCount', '리워드'), align: 'right', width: 64, cell: (row) => <span className="text-[12px] tabular-nums" data-testid="funding-reward-count">{count(row.rewardCount)}</span> },
    {
      key: 'period',
      header: t('funding.columns.period', '기간'),
      width: 190,
      cell: (row) => (
        <span className="whitespace-nowrap text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }} data-testid="funding-period">
          {day(row.startAt)} ~ {day(row.endAt)}
        </span>
      ),
    },
    { key: 'launchedAt', header: t('funding.columns.launchedAt', '런칭일'), width: 100, sortable: true, cell: (row) => <span className="whitespace-nowrap text-[12px] tabular-nums">{day(row.launchedAt)}</span> },
    { key: 'closedAt', header: t('funding.columns.closedAt', '마감일'), width: 100, sortable: true, cell: (row) => <span className="whitespace-nowrap text-[12px] tabular-nums">{day(row.closedAt)}</span> },
  ];

  const dateRange = (from: keyof FundingListFilter, to: keyof FundingListFilter, label: string) => (
    <div className="flex items-center gap-1.5">
      <Input type="date" value={draft[from] as string} onChange={(e) => patch({ [from]: e.target.value } as Partial<FundingListFilter>)} aria-label={`${label} ${t('funding.filters.from', '시작')}`} className="w-full max-w-[170px]" />
      <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>~</span>
      <Input type="date" value={draft[to] as string} onChange={(e) => patch({ [to]: e.target.value } as Partial<FundingListFilter>)} aria-label={`${label} ${t('funding.filters.to', '종료')}`} className="w-full max-w-[170px]" />
    </div>
  );

  return (
    <PageShell
      title={t('funding.list.title')}
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/funding-dashboard" aria-label={t('funding.actions.openDashboard')}>
              {t('funding.actions.openDashboard')}
            </Link>
          </Button>
          {canWrite ? (
            <Button asChild variant="cta">
              <Link to="/funding-campaigns/new" aria-label={t('funding.actions.create')}>
                {t('funding.actions.create')}
              </Link>
            </Button>
          ) : null}
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          apply(draft);
        }}
      >
        <SearchGrid
          aria-label={t('funding.filters.label')}
          testId="funding-search-grid"
          footer={
            <div className="flex items-center gap-2">
              <Button type="submit" size="sm" data-testid="funding-search-submit">
                {t('funding.filters.submit', '검색')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(EMPTY_FUNDING_FILTER);
                  apply(EMPTY_FUNDING_FILTER);
                }}
              >
                {t('funding.filters.reset', '초기화')}
              </Button>
            </div>
          }
        >
          <SearchGridItem label={t('funding.filters.keyword')} htmlFor="funding-keyword">
            <Input
              id="funding-keyword"
              type="search"
              value={draft.keyword}
              onChange={(e) => patch({ keyword: e.target.value })}
              placeholder={t('funding.filters.keywordPlaceholder', '캠페인 제목 검색')}
              className="w-full max-w-[360px]"
            />
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.status')}>
            <Choice testId="funding-filter-status">
              {FUNDING_STATUS_VALUES.map((s) => (
                <label key={s} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={draft.statuses.includes(s)}
                    onChange={() =>
                      patch({ statuses: draft.statuses.includes(s) ? draft.statuses.filter((v) => v !== s) : [...draft.statuses, s] })
                    }
                  />
                  {statusLabel(s)}
                </label>
              ))}
            </Choice>
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.artistId', '아티스트')}>
            <div className="max-w-[360px]">
              <ArtistSelector value={draft.artistId} onChange={(id) => patch({ artistId: id })} testId="funding-filter-artist" />
            </div>
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.category', '구분')}>
            <div className="max-w-[240px]">
              <RadixSelect
                id="funding-filter-category"
                value={draft.category}
                onChange={(v) => patch({ category: v as FundingCategory | '' })}
                options={[
                  { value: '', label: t('funding.filters.categoryAll', '전체 구분') },
                  ...FUNDING_CATEGORY_VALUES.map((c) => ({ value: c, label: categoryLabel(c) })),
                ]}
              />
            </div>
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.period', '펀딩 기간')}>
            {dateRange('periodFrom', 'periodTo', t('funding.filters.period', '펀딩 기간'))}
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.visibility', '공개 여부')}>
            <Choice testId="funding-filter-visibility">
              {(['ALL', 'PUBLIC', 'PRIVATE'] as const).map((v: FundingVisibility) => (
                <label key={v} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <input type="radio" name="funding-visibility" checked={draft.visibility === v} onChange={() => patch({ visibility: v })} />
                  {v === 'ALL'
                    ? t('funding.visibility.all', '전체')
                    : v === 'PUBLIC'
                      ? t('funding.visibility.public', '공개')
                      : t('funding.visibility.private', '비공개')}
                </label>
              ))}
            </Choice>
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.launchedAt', '런칭일')}>
            {dateRange('launchedFrom', 'launchedTo', t('funding.filters.launchedAt', '런칭일'))}
          </SearchGridItem>
          <SearchGridItem label={t('funding.filters.closedAt', '마감일')}>
            {dateRange('closedFrom', 'closedTo', t('funding.filters.closedAt', '마감일'))}
          </SearchGridItem>
        </SearchGrid>
      </form>

      <div className="mt-4">
        {isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[44px] w-full" />
            ))}
          </div>
        ) : isError ? (
          <div
            role="alert"
            aria-live="polite"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>{t('funding.list.error')}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t('funding.actions.retry')}
            </Button>
          </div>
        ) : (
          <>
            <p className="mb-2 text-[13px]" style={{ color: 'var(--content-muted)' }} data-testid="funding-total">
              {t('funding.list.total', { n: total.toLocaleString('ko-KR'), defaultValue: '검색 {{n}}건' })}
            </p>
            <DataTable
              columns={columns}
              rows={pageRows}
              rowKey={(row) => row.campaignId}
              emptyState={t('funding.list.empty')}
              stickyHeader
              sort={sort as DataTableSort | null}
              onSortChange={(key) => {
                if (!SORTABLE.has(key)) return;
                setSort((prev) =>
                  prev && prev.key === key
                    ? { key: prev.key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
                    : { key: key as FundingSortKey, direction: 'desc' },
                );
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      <Pagination total={total} currentPage={page} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4" />
    </PageShell>
  );
};
